import { Platform, View } from "@-/com.npmjs.react-native";
import Animated, {
  interpolate,
  type SharedValue,
  useAnimatedStyle,
} from "@-/com.npmjs.react-native-reanimated";
import { ToolbarIconButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { ReactNode } from "react";
import { CHINA_MAP_SHEET_BUTTONS_GAP } from "#china-map-sheet-heights";

/**
 * Google Maps–style buttons riding just above the map's bottom sheet as it rests or is dragged.
 * The right column frames the map: the whole map, and the learner's surroundings (zooming in and
 * out is by pinch). The More menu sits opposite, over the sheet's left edge, level with the lower
 * camera button.
 */
export function ChinaMapSheetButtons({
  moreActions,
  onShowChina,
  onShowCurrentLocation,
  regionName,
  sheetTop,
  topInset,
}: {
  /** The map's More menu (`MapMoreActions`). */
  moreActions: ReactNode;
  onShowChina: () => void;
  /** `null` while the learner has no location (before choosing a start). */
  onShowCurrentLocation: (() => void) | null;
  /** The open map's region in a sentence, e.g. "mainland China". */
  regionName: string;
  /** The draggable sheet's top edge, in px from the top of the page. */
  sheetTop: SharedValue<number>;
  /** Height of the floating toolbar: the buttons fade out as the sheet is dragged up to it. */
  topInset: number;
}) {
  const { diameter } = useToolbarGridMetrics();
  const buttonCount = onShowCurrentLocation === null ? 1 : 2;
  const stackHeight = buttonCount * diameter + (buttonCount - 1) * CHINA_MAP_SHEET_BUTTONS_GAP;
  const followSheetStyle = useAnimatedStyle(() => {
    if (Platform.OS === "web") {
      return {};
    }
    const top = sheetTop.get() - CHINA_MAP_SHEET_BUTTONS_GAP - stackHeight;
    return {
      opacity: interpolate(top, [topInset, topInset + diameter], [0, 1], "clamp"),
      transform: [{ translateY: top }],
    };
  });
  return (
    <Animated.View
      className={`absolute inset-x-4 flex-row items-end justify-between ${Platform.OS === "web" ? "bottom-4" : "top-0"}`}
      pointerEvents="box-none"
      style={[{ height: stackHeight }, followSheetStyle]}
      testID="map-sheet-buttons"
    >
      <View pointerEvents="box-none" testID="map-more-actions">
        {moreActions}
      </View>
      <View
        className="items-end"
        pointerEvents="box-none"
        style={{ gap: CHINA_MAP_SHEET_BUTTONS_GAP }}
        testID="map-camera-buttons"
      >
        <ToolbarIconButton
          accessibilityLabel={`Show all of ${regionName}`}
          iconSize={22}
          name="MdZoomOutMap"
          onPress={onShowChina}
          size="menu"
          testID="map-camera-show-china-button"
        />
        {onShowCurrentLocation === null ? null : (
          <ToolbarIconButton
            accessibilityLabel="Show my location"
            iconSize={22}
            name="MdMyLocation"
            onPress={onShowCurrentLocation}
            size="menu"
            testID="map-camera-my-location-button"
          />
        )}
      </View>
    </Animated.View>
  );
}
